
import React, { useRef, useEffect, useState } from 'react';
import { Message } from '../types';
import SouthIndianCharts from './SouthIndianChart';

interface ChatInterfaceProps {
  messages: Message[];
  onSendMessage: (text: string) => void;
  isLoading: boolean;
  onReset: () => void;
}

// Helper to parse bold text into React elements
const parseTextContent = (text: string) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="text-gold-400 font-bold">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const KEY_DETAILS = [
  'rasi', 'moon sign', 'ராசி', 'රාශි',
  'nakshatra', 'star', 'நட்சத்திரம்', 'නැකත',
  'lagna', 'ascendant', 'லக்னம்', 'ලග්න',
  'sun sign', 'சூரிய ராசி', 'சூரிய', 'sun', 'රවි',
  'tithi', 'திதி',
  'tithi deity', 'திதி தெய்வம்',
  'karana', 'கரணம்',
  'yoga', 'யோகம்',
  'yoga deity', 'யோக தேவதை'
];

const isKeyDetail = (text: string) => {
  const lower = text.toLowerCase();
  // Check if the text contains any of the key terms
  // We use a simple includes check which is effective for the structured list output
  return KEY_DETAILS.some(k => lower.includes(k));
};

// Component to render formatted model response with rich styling
const FormattedModelResponse: React.FC<{ text: string }> = ({ text }) => {
  // Clean text from any accidental XML/JSON
  const cleanText = text.replace(/<chart>[\s\S]*?<\/chart>/g, '').trim();

  // Split remaining text by headers
  const sections = cleanText.split(/(?=###)/g);

  return (
    <div className="space-y-6 w-full">
      {sections.map((section, idx) => {
        const lines = section.split('\n').filter(l => l.trim());
        if (lines.length === 0) return null;

        let header = null;
        let contentLines = lines;

        // Detect and strip header
        if (lines[0].trim().startsWith('###')) {
          header = lines[0].trim()
            .replace(/^###\s*/, '')
            .replace(/\*\*/g, '')
            .replace(/:$/, '')
            .trim();
          contentLines = lines.slice(1);
        }

        const isAlternate = idx % 2 === 1;
        
        return (
          <div 
            key={idx} 
            className={`
              rounded-xl transition-all duration-500
              ${isAlternate 
                ? 'bg-gradient-to-br from-white/5 to-transparent border border-white/5 p-6 md:p-8 shadow-sm' 
                : 'p-4 md:p-6'
              }
            `}
          >
            {header && (
              <h3 className="text-xl md:text-2xl font-serif text-gold-500 mb-6 pb-2 border-b border-white/10 flex items-center gap-3">
                {isAlternate && <span className="text-lg text-gold-600 opacity-70">✦</span>}
                {header}
              </h3>
            )}
            
            <div className="space-y-4 text-gray-200 leading-relaxed font-sans text-base md:text-lg">
              {contentLines.map((line, lineIdx) => {
                const trimmed = line.trim();
                
                if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
                  const content = trimmed.replace(/^[\*\-]\s+/, '');
                  
                  // Check if this list item is a Key Detail (Rasi, Nakshatra, Lagna, Sun Sign)
                  if (isKeyDetail(content)) {
                     // STRIP MARKDOWN ASTERISKS before splitting
                     const cleanContent = content.replace(/\*\*/g, '');
                     
                     // Split by first colon or hyphen
                     // Using regex search to find split index safely
                     const match = cleanContent.match(/[:|-]/);
                     let label = cleanContent;
                     let value = "";

                     if (match && match.index !== undefined) {
                         label = cleanContent.substring(0, match.index).trim();
                         value = cleanContent.substring(match.index + 1).trim();
                     }

                     if (value) {
                         return (
                            <div key={lineIdx} className="flex gap-4 pl-2 items-start group my-1">
                              <span className="text-gold-500 mt-2 text-[10px] shrink-0 group-hover:scale-125 transition-transform">◆</span>
                              <div className="flex-1 font-sans text-base md:text-lg leading-relaxed">
                                <span className="font-bold text-gray-100">{label}:</span>
                                <span className="text-gold-400 ml-2 font-bold">{value}</span>
                              </div>
                            </div>
                         );
                     } else {
                         return (
                            <div key={lineIdx} className="flex gap-4 pl-2 items-start group my-1">
                                <span className="text-gold-500 mt-2 text-[10px] shrink-0 group-hover:scale-125 transition-transform">◆</span>
                                <div className="flex-1 font-sans text-base md:text-lg leading-relaxed text-gold-400 font-bold">
                                    {label}
                                </div>
                            </div>
                         );
                     }
                  }

                  return (
                    <div key={lineIdx} className="flex gap-4 pl-2 items-start group">
                      <span className="text-gold-500 mt-2 text-[10px] shrink-0 group-hover:scale-125 transition-transform">◆</span>
                      <p className="flex-1 text-gray-300 group-hover:text-gray-100 transition-colors">{parseTextContent(content)}</p>
                    </div>
                  );
                }
                
                return (
                  <p key={lineIdx} className={lineIdx === 0 && !header ? 'first-letter:text-3xl first-letter:font-serif first-letter:text-gold-500 first-letter:mr-1' : ''}>
                    {parseTextContent(trimmed)}
                  </p>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({ messages, onSendMessage, isLoading, onReset }) => {
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoading) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage) {
        if (lastMessage.role === 'model') {
          const element = document.getElementById(`msg-${lastMessage.id}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        } else {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [messages, isLoading]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || isLoading) return;
    onSendMessage(inputText);
    setInputText('');
  };

  return (
    <div className="flex flex-col h-screen max-w-6xl mx-auto relative z-10 bg-black/40 backdrop-blur-xl shadow-2xl border-x border-white/5">
      {/* Header */}
      <header className="flex-none p-4 md:p-6 bg-black/60 border-b border-white/10 flex justify-between items-center backdrop-blur-md sticky top-0 z-20">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-900 to-indigo-900 border border-gold-500/30 flex items-center justify-center shadow-[0_0_15px_rgba(255,215,0,0.2)]">
            <span className="text-2xl animate-pulse">🔮</span>
          </div>
          <div>
            <h2 className="font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-yellow-100 text-xl tracking-wide">Vedic Oracle</h2>
            <div className="flex items-center gap-2">
                 <span className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></span>
                 <p className="text-xs text-gray-400 font-sans tracking-widest uppercase">Live Connection</p>
            </div>
          </div>
        </div>
        <button 
            onClick={onReset}
            className="group flex items-center gap-2 text-gold-500 hover:text-white transition-all text-xs md:text-sm px-4 py-2 rounded-full border border-gold-500/30 hover:bg-gold-500/10 hover:border-gold-500/60"
        >
            <span className="group-hover:rotate-180 transition-transform duration-500">↻</span> New Chart
        </button>
      </header>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8 space-y-8 custom-scrollbar">
        {messages.map((msg) => (
          <div
            key={msg.id}
            id={`msg-${msg.id}`}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} scroll-mt-32`}
          >
            <div
              className={`
                relative rounded-2xl transition-all
                ${msg.role === 'user'
                  ? 'bg-gradient-to-r from-indigo-900/80 to-purple-900/80 text-white rounded-tr-sm max-w-[85%] md:max-w-[60%] border border-white/10 p-4 md:p-6 shadow-lg'
                  : 'w-full bg-transparent text-gray-100 p-0'
                }
              `}
            >
              <div className="">
                {/* RENDER CHARTS IF DATA EXISTS */}
                {msg.chartData && (
                    <div className="mb-6">
                        <SouthIndianCharts data={msg.chartData} />
                    </div>
                )}

                {msg.role === 'user' ? (
                   <div className="font-sans text-base md:text-lg whitespace-pre-wrap leading-relaxed">{msg.text}</div>
                ) : (
                   <FormattedModelResponse text={msg.text} />
                )}
              </div>
              
              {msg.role === 'user' && (
                  <div className="text-[10px] text-right mt-2 opacity-50">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
              )}
            </div>
          </div>
        ))}
        
        {isLoading && (
            <div className="w-full p-4 md:p-8">
                 <div className="flex items-center gap-3 text-gold-500/70 font-serif animate-pulse">
                    <div className="flex gap-1">
                        <span className="w-2 h-2 bg-gold-500 rounded-full animate-[bounce_1s_infinite_0ms]"></span>
                        <span className="w-2 h-2 bg-gold-500 rounded-full animate-[bounce_1s_infinite_200ms]"></span>
                        <span className="w-2 h-2 bg-gold-500 rounded-full animate-[bounce_1s_infinite_400ms]"></span>
                    </div>
                    <span>Consulting the stars...</span>
                 </div>
            </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="flex-none p-4 md:p-6 bg-black/60 border-t border-white/10 backdrop-blur-xl">
        <form onSubmit={handleSubmit} className="relative flex gap-3 max-w-4xl mx-auto w-full">
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Ask about your destiny..."
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-6 py-4 text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gold-500/50 focus:bg-white/10 transition-all font-sans text-base md:text-lg shadow-inner"
            disabled={isLoading}
          />
          <button
            type="submit"
            disabled={!inputText.trim() || isLoading}
            className="bg-gradient-to-b from-gold-500 to-gold-600 text-black rounded-xl px-6 py-4 flex items-center justify-center shadow-[0_0_20px_rgba(255,215,0,0.3)] hover:shadow-[0_0_30px_rgba(255,215,0,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatInterface;
